import { useState } from "react";
import { MapPin } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { TruckCameo } from "@/components/TruckCameo";
import { countries } from "@/data/content";

const HUB = [600, 330];

const shapes = {
  saudi: "M120,90 L470,60 L520,140 L560,220 L520,300 L450,380 L300,420 L180,380 L120,280 L100,180 Z",
  uae: "M560,300 L665,290 L685,340 L620,372 L558,352 Z",
  oman: "M685,340 L765,360 L785,445 L720,525 L660,470 L642,400 Z",
  qatar: "M566,232 L582,232 L580,268 L562,262 Z",
  kuwait: "M472,58 L522,54 L527,96 L480,102 Z",
};

const routes = [
  { to: [430, 260], curve: "M600,330 Q500,320 430,260" },
  { to: [700, 400], curve: "M600,330 Q660,360 700,400" },
  { to: [572, 252], curve: "M600,330 Q600,290 572,252" },
  { to: [500, 82], curve: "M600,330 Q560,200 500,82" },
  { to: [555, 205], curve: "M600,330 Q590,260 555,205" },
];

export const Coverage = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="coverage" data-testid="coverage-section" className="relative overflow-hidden bg-gradient-to-b from-white via-ice/40 to-mist py-28 md:py-40">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          number="04"
          eyebrow="GCC Coverage"
          title={<>One hub in Abu Dhabi. <span className="text-royal/40">Six nations, one promise.</span></>}
          description="Glowing routes depart daily from our Abu Dhabi headquarters toward every capital in the Gulf."
        />

        <div className="mb-14 flex justify-center">
          <TruckCameo from="right" />
        </div>

        <div className="grid items-center gap-12 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            <div className="glass-card hex-card relative p-4 md:p-8" data-testid="gcc-map">
              <svg viewBox="0 0 860 580" className="w-full" style={{ maxHeight: "520px" }}>
                <g transform="translate(-60,-35)">
                <path d={shapes.saudi} fill="#1A365D" fillOpacity="0.08" stroke="#1A365D" strokeOpacity="0.3" strokeWidth="1.5" />
                <path d={shapes.uae} fill="#1A365D" fillOpacity="0.14" stroke="#1A365D" strokeOpacity="0.4" strokeWidth="1.5" />
                <path d={shapes.oman} fill="#1A365D" fillOpacity="0.08" stroke="#1A365D" strokeOpacity="0.3" strokeWidth="1.5" />
                <path d={shapes.qatar} fill="#1A365D" fillOpacity="0.14" stroke="#1A365D" strokeOpacity="0.4" strokeWidth="1.5" />
                <path d={shapes.kuwait} fill="#1A365D" fillOpacity="0.08" stroke="#1A365D" strokeOpacity="0.3" strokeWidth="1.5" />
                <circle cx="555" cy="205" r="7" fill="#1A365D" fillOpacity="0.14" stroke="#1A365D" strokeOpacity="0.4" />

                {routes.map((r, i) => (
                  <g key={i}>
                    <path d={r.curve} fill="none" stroke="#EAB308" strokeWidth="2.5" strokeDasharray="8 8" className="route-glow animate-dash-move" />
                    <circle r="4" fill="#EAB308" className="route-glow">
                      <animateMotion dur={`${3 + i * 0.6}s`} repeatCount="indefinite" path={r.curve} />
                    </circle>
                  </g>
                ))}

                <g>
                  <circle cx={HUB[0]} cy={HUB[1]} r="10" fill="#EAB308" className="animate-pulse-node" />
                  <circle cx={HUB[0]} cy={HUB[1]} r="20" fill="none" stroke="#EAB308" strokeOpacity="0.5" strokeWidth="1.5" className="animate-pulse-node" />
                  <text x={HUB[0]} y={HUB[1] + 34} textAnchor="middle" className="fill-navy font-jakarta text-[13px] font-bold">ABU DHABI HQ</text>
                </g>

                {countries.filter((c) => c.name !== "UAE").map((c) => (
                  <g key={c.name} className="cursor-pointer" onMouseEnter={() => setActive(c.name)} onMouseLeave={() => setActive(null)}>
                    <circle cx={c.node[0]} cy={c.node[1]} r="7" fill={active === c.name ? "#EAB308" : "#0A192F"} className="transition-all duration-300" />
                    <text x={c.node[0]} y={c.node[1] - 16} textAnchor="middle" className="fill-royal font-jakarta text-[12px] font-bold uppercase tracking-wider">
                      {c.name}
                    </text>
                  </g>
                ))}
                </g>
              </svg>
              <p className="mt-4 text-center font-jakarta text-sm font-bold uppercase tracking-[0.3em] text-royal/60">
                Serving the Entire GCC
              </p>
            </div>
          </Reveal>

          <div className="space-y-4 lg:col-span-2">
            {countries.map((c, i) => (
              <Reveal key={c.name} delay={i * 0.07}>
                <div
                  data-testid={`coverage-country-${c.name.toLowerCase().replace(" ", "-")}`}
                  onMouseEnter={() => setActive(c.name)}
                  onMouseLeave={() => setActive(null)}
                  className={`glass-card hex-card flex items-center gap-4 p-5 transition-all duration-300 hover:-translate-y-1 hover:border-gold/40 hover:shadow-xl ${active === c.name ? "border-gold/50 shadow-xl shadow-gold/10" : ""}`}
                >
                  <span className={`grid h-11 w-11 shrink-0 place-items-center hex-clip transition-colors duration-300 ${active === c.name ? "bg-gold" : "bg-navy"}`}>
                    <MapPin className={`h-5 w-5 ${active === c.name ? "text-navy" : "text-sky"}`} />
                  </span>
                  <div>
                    <h3 className="font-jakarta text-base font-bold text-navy">{c.name}</h3>
                    <p className="text-xs text-royal/60 md:text-sm">{c.detail}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
